/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

angular.module('prism-ui.groups').service('GroupsService', function (this: JS5Object,
                                                                         _: JS5Object,
                                                                         X2JS: any,
                                                                         AggregationResourceFactory: JS5Object,
                                                                         ReachPermissionsResourceFactory: JS5Object,
                                                                         SecurityRolesResourceFactory: JS5Object,
                                                                         NetworkResourceFactory: JS5Object,
                                                                         PackageResourceFactory: JS5Object,
                                                                         PermissionsService: JS5Object,
                                                                         ContentResourceFactory: JS5Object,
                                                                         PaginationService: JS5Object,
                                                                         UsersService: JS5Object,
                                                                         $q: JS5Object) {
    this.XJS = new X2JS();
    this.originalEditData;
    this.editorForm;
    this.breadcrumbName;

    const getTargetClass = (target: string) => {
        if (target.startsWith('[') && target.endsWith(']')) {
            return target.substring(1, target.indexOf('.'));
        } else {
            return target;
        }
    }

    let generatePermissionContextMap = (permissionContexts: Array<JS5Object>) => {
        let permissionContextObject: JS5Object = {};

        let permissionContextGroups = _.groupBy(permissionContexts, permissionContext => getTargetClass(permissionContext.target));

        _.forEach(permissionContextGroups, (contexts: JS5Object, key: string) => {
            if (key === 'Authorities') {
                permissionContextObject[key] = _.keyBy(contexts, context => this.XJS.xml2js(context.targetCriteria).and.condition._property);
            } else {
                permissionContextObject[key] = _.keyBy(contexts, context => context.target);
            }
        });

        return permissionContextObject;
    };

    const responseHandler = (data: JS5Object, responseHeaders: JS5Function) => {
        PaginationService.parseResponseHeaders(responseHeaders());
        PaginationService.pageData = data;
    };

    const getDataObjectInstances = (group: JS5Object) => {
        const permissionsPromise = _.has(group, 'id') ? SecurityRolesResourceFactory.getRolePermissions({id: group.id}).$promise : {rows: []};
        const networkPromise = NetworkResourceFactory.query({fetchLimit: -1, view: 'MINIMAL'}).$promise;
        const categoryTypePromise = PackageResourceFactory.getCategories({view: 'EXTENDED'}).$promise;
        const seriesPromise = ContentResourceFactory.query({fetchLimit: -1, type: 'Series', view: 'MINIMAL'}).$promise;
        const moviePromise = ContentResourceFactory.query({fetchLimit: -1, type: 'Movie/Special', view: 'MINIMAL'}).$promise;
        const adPromise = ContentResourceFactory.query({fetchLimit: -1, type: 'Ad/PreRoll', view: 'MINIMAL'}).$promise;
        const aggregationSetupPromise = AggregationResourceFactory.query({fetchLimit: -1}).$promise;

        return $q.all({
            group: group,
            permissionContexts: permissionsPromise,
            network: networkPromise,
            packageCategoryType: categoryTypePromise,
            series: seriesPromise,
            movie: moviePromise,
            advertisement: adPromise,
            aggregationSetup: aggregationSetupPromise
        })
        .then((data: JS5Object) => {
            return {
                group: data.group,
                permissionContexts: data.permissionContexts.rows,
                permissionContextMap: generatePermissionContextMap(data.permissionContexts.rows),
                mappingData: {
                    network: data.network,
                    longFormType: [data.packageCategoryType[0]],
                    longForm: data.packageCategoryType[0].categories,
                    shortFormType: [data.packageCategoryType[1]],
                    shortForm: data.packageCategoryType[1].categories,
                    series: _.keyBy(data.series, data => `[Series.null.${data.id}]`),
                    movie: _.keyBy(data.movie, data => `[Movie.null.${data.id}]`),
                    advertisement: _.keyBy(data.advertisement, data => `[Advertisement.null.${data.id}]`),
                    aggregationSetup: _.keyBy(data.aggregationSetup, data => `[AggregationSetup.null.${data.id}]`)
                }
            };
        });
    };

    const getAllUsersWithGroups = (showHidden: boolean) => {
        let usersCriteria = {
            fetchLimit: -1,
            includeRoles: true,
            includeArchived: showHidden
        };
        return UsersService.getAllUsers(usersCriteria);
    }

    const addUsersToGroups = (groups: JS5Object[], usersWithGroups: JS5Object[]) => {
        let groupsById: JS5Object[] = {};
        _.forEach(groups, (group: JS5Object) => {
            group.users = [];
            groupsById[group.id] = group;
        })
        usersWithGroups.forEach((user: JS5Object) => {
            user.roles.forEach((userGroup: JS5Object) => {
                if (_.has(groupsById, userGroup.id)) {
                    groupsById[userGroup.id].users.push(user);
                }
            });
            delete user.roles;
        });
    };

    this.getAllGroups = (criteria: JS5Object) => {
        if (_.get(criteria, 'includeUsers')) {
            let groupResponseHeaders: JS5Function;
            const groupResponseHandler = (data: JS5Object, responseHeaders: JS5Function) => {
                groupResponseHeaders = responseHeaders;
            }
            delete criteria.includeUsers;
            return $q.all({
                groups: SecurityRolesResourceFactory.query(criteria, groupResponseHandler).$promise,
                users: getAllUsersWithGroups(_.get(criteria, 'showHidden'))
            }).then((data: JS5Object) => {
                addUsersToGroups(data.groups, data.users);
                responseHandler(data.groups, groupResponseHeaders);
                return data.groups;
            });
        } else {
            return SecurityRolesResourceFactory.query(criteria, responseHandler).$promise;
        }
    };

    this.getGroup = (groupId: number, criteria: JS5Object) => {
        if (_.get(criteria, 'includeUsers')) {
            return $q.all({
                group: SecurityRolesResourceFactory.getRole({id: groupId}).$promise,
                users: getAllUsersWithGroups(false)
            }).then((data: JS5Object) => {
                addUsersToGroups([data.group], data.users);
                return data.group;
            });
        } else {
            return SecurityRolesResourceFactory.getRole({id: groupId}).$promise;
        }
    };

    this.getGroupAndMappingData = (groupId: number) => {
        if (_.toNumber(groupId) === -1) {
            return getDataObjectInstances({
                name: '',
                permissionContexts: []
            });
        } else {
            return this.getGroup(groupId)
            .then(getDataObjectInstances);
        }
    };

    this.isSystemOperatorGroup = (groupId: number) => {
        return SecurityRolesResourceFactory.getRolePermissions({id: groupId}).$promise.then((permissions: JS5Object) => {
            return _.some(permissions.rows, (permissionContext: JS5Object) => {
                let viewState = _.get(this.XJS.xml2js(permissionContext.targetCriteria), 'and.condition._property');
                return viewState === 'DEBUG_SYSTEM_OPERATIONS_VIEW_STATE';
            });
        });
    };

    this.updateMappingData = (data: JS5Object) => {
        data.permissionContextMap = generatePermissionContextMap(data.permissionContexts);
    }

    const savePermissions = (group: JS5Object, permissionContexts: JS5Object[]) => {
        let permissionPromises = _.map(permissionContexts, (permissionContext: JS5Object) => {
            permissionContext.roleUuid = group.uuid;
            if (permissionContext.uuid) {
                return ReachPermissionsResourceFactory.updatePermission(permissionContext).$promise;
            } else {
                return ReachPermissionsResourceFactory.createPermission(permissionContext).$promise;
            }
        });
        return $q.all(permissionPromises);
    };

    const getUpdatedEditData = (group: JS5Object) => {
        return SecurityRolesResourceFactory.getRolePermissions({id: group.id}).$promise
            .then((permissions: JS5Object) => {
                return {
                    group: group,
                    permissionContexts: permissions.rows
                };
            });
    };

    this.createGroup = (group: JS5Object, permissionContexts: JS5Object[]) => {
        return SecurityRolesResourceFactory.createRole(group).$promise
            .then((resultGroup: JS5Object) => {
                return savePermissions(resultGroup, permissionContexts).then(() => {
                    return getUpdatedEditData(resultGroup);
                });
            });
    };

    this.updateGroup = (group: JS5Object, oldPermissionContexts: JS5Object[], newPermissionContexts: JS5Object[]) => {
        let updateRolePromise = SecurityRolesResourceFactory.updateRole(group).$promise;
        let savePermissionsPromise = savePermissions(group, newPermissionContexts);
        let deleteContextPromises = _.chain(oldPermissionContexts)
            .filter((oldPermissionContext: JS5Object) => {
                return !_.find(newPermissionContexts, { uuid: oldPermissionContext.uuid });
            }).map((oldPermissionContext: JS5Object) => {
                return ReachPermissionsResourceFactory.deletePermission({uuid: oldPermissionContext.uuid}).$promise;
            })
            .value();
        return $q.all([
            updateRolePromise,
            savePermissionsPromise,
            ...deleteContextPromises
        ]).then((data: JS5Object[]) => getUpdatedEditData(data[0]));
    };

    this.deleteGroup = (groupId: number) => {
        return SecurityRolesResourceFactory.deleteRole({id: groupId}).$promise;
    };
});
